/* Task ID: GCA 020 - Sticky ATC Mobile JavaScript */

class StickyATCMobile {
  constructor() {
    this.stickyElement = document.querySelector('.sticky-atc-mobile');
    if (!this.stickyElement) return;

    this.scrollPercentage = parseInt(this.stickyElement.dataset.scrollPercentage) || 5;
    this.isVisible = false;
    this.isInitialized = false;
    
    this.init();
  }

  init() {
    if (this.isInitialized) return;
    
    // Only initialize on mobile
    if (window.innerWidth > 750) return;

    this.setupEventListeners();
    this.syncWithMainForm();
    this.isInitialized = true;
  }

  setupEventListeners() {
    // Scroll detection
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Resize handling
    window.addEventListener('resize', this.debounce(() => {
      if (window.innerWidth > 750) {
        this.hide();
      } else if (window.innerWidth <= 750 && !this.isInitialized) {
        this.init();
      }
    }, 250));

    // Button click handling
    const button = this.stickyElement.querySelector('.sticky-atc-mobile__button');
    if (button) {
      button.addEventListener('click', (e) => {
        this.handleAddToCart(e);
      });
    }

    // Listen for variant changes from main product form
    document.addEventListener('variant:change', (e) => {
      this.updateVariant(e.detail);
    });

    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.hideTemporarily();
    });
  }

  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = (scrollTop / documentHeight) * 100;

    if (scrollPercent >= this.scrollPercentage && !this.isVisible) {
      this.show();
    } else if (scrollPercent < this.scrollPercentage && this.isVisible) {
      this.hide();
    }
  }

  show() {
    if (this.isVisible) return;
    
    this.stickyElement.classList.add('show');
    this.isVisible = true;
    
    // Announce to screen readers
    this.stickyElement.setAttribute('aria-live', 'polite');
    this.stickyElement.setAttribute('aria-label', 'Sticky add to cart button appeared');
  }

  hide() {
    if (!this.isVisible) return;
    
    this.stickyElement.classList.remove('show');
    this.isVisible = false;
    this.stickyElement.removeAttribute('aria-live');
    this.stickyElement.removeAttribute('aria-label');
  }

  hideTemporarily() {
    this.hide();
    // Show again after 2 seconds if still scrolled enough
    setTimeout(() => {
      this.handleScroll();
    }, 2000);
  }

  syncWithMainForm() {
    const mainForm = document.querySelector('product-form form[data-type="add-to-cart-form"]');
    if (!mainForm) return;

    // Sync quantity from main form when sticky button is clicked
    const mainQuantityInput = mainForm.querySelector('input[name="quantity"]');
    const stickyButton = this.stickyElement.querySelector('.sticky-atc-mobile__button');
    
    if (stickyButton && mainQuantityInput) {
      stickyButton.addEventListener('click', () => {
        // Update hidden quantity input if needed
        const quantity = mainQuantityInput.value || 1;
        stickyButton.setAttribute('data-quantity', quantity);
      });
    }

    // Listen for main form changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
          this.updateButtonState();
        }
      });
    });

    const mainButton = mainForm.querySelector('button[type="submit"]');
    if (mainButton) {
      observer.observe(mainButton, { attributes: true });
    }
  }

  updateVariant(variantData) {
    const button = this.stickyElement.querySelector('.sticky-atc-mobile__button');
    const buttonText = this.stickyElement.querySelector('.sticky-atc-mobile__button-text');
    const priceElement = this.stickyElement.querySelector('.sticky-atc-mobile__price');
    
    if (!button || !buttonText) return;

    // Update button state
    if (variantData.available) {
      button.disabled = false;
      buttonText.textContent = button.dataset.originalText || 'Add To Cart';
    } else {
      button.disabled = true;
      buttonText.textContent = 'Sold out';
    }

    // Update variant ID
    button.setAttribute('data-variant-id', variantData.id);
    this.stickyElement.setAttribute('data-variant-id', variantData.id);

    // Update price if available
    if (variantData.price && priceElement) {
      // This would need to be updated based on your price rendering logic
      // For now, we'll let the main form handle price updates
    }
  }

  updateButtonState() {
    const mainForm = document.querySelector('product-form form[data-type="add-to-cart-form"]');
    const mainButton = mainForm?.querySelector('button[type="submit"]');
    const stickyButton = this.stickyElement.querySelector('.sticky-atc-mobile__button');
    
    if (!mainButton || !stickyButton) return;

    // Sync disabled state
    stickyButton.disabled = mainButton.disabled;
    
    // Sync button text
    const buttonText = stickyButton.querySelector('.sticky-atc-mobile__button-text');
    const mainButtonText = mainButton.querySelector('span');
    
    if (buttonText && mainButtonText) {
      buttonText.textContent = mainButtonText.textContent;
    }
  }

  handleAddToCart(e) {
    const button = e.currentTarget;
    const mainForm = document.querySelector('product-form form[data-type="add-to-cart-form"]');
    
    if (!mainForm) {
      console.warn('Main product form not found');
      return;
    }

    // Add loading state
    button.classList.add('loading');
    
    // Let the main form handle the submission
    // The button is already connected via the form attribute
    
    // Remove loading state after a delay (will be removed by cart update event too)
    setTimeout(() => {
      button.classList.remove('loading');
    }, 3000);
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyATCMobile();
  });
} else {
  new StickyATCMobile();
}

// Re-initialize on theme section reloads (Shopify theme editor)
document.addEventListener('shopify:section:load', () => {
  new StickyATCMobile();
});